import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PageFrame } from '@components/ui/PageFrame/PageFrame';
import { Button } from '@components/ui/Button/Button';
import styles from './styles.module.css';

interface IRouteErrorBoundaryProps {
  /** Identifies the page being shown (the pathname). A new key — the user
   *  navigated — clears a caught error, so one broken page never takes the
   *  rest of the app down with it. */
  resetKey: string;
  children: ReactNode;
}

interface IRouteErrorBoundaryState {
  failed: boolean;
  /** The `resetKey` the state belongs to. */
  key: string;
}

/**
 * Catches a render error in the routed page, so the shell (sidebar, top bar)
 * stays up and the user gets a way back instead of a blank window. Without a
 * boundary React unmounts the whole tree on any throw.
 */
export class RouteErrorBoundary extends Component<IRouteErrorBoundaryProps, IRouteErrorBoundaryState> {
  override state: IRouteErrorBoundaryState = { failed: false, key: this.props.resetKey };

  static getDerivedStateFromProps(
    props: IRouteErrorBoundaryProps,
    state: IRouteErrorBoundaryState,
  ): Partial<IRouteErrorBoundaryState> | null {
    return props.resetKey === state.key ? null : { failed: false, key: props.resetKey };
  }

  static getDerivedStateFromError(): Partial<IRouteErrorBoundaryState> {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('Page crashed', error, info.componentStack);
  }

  override render() {
    return this.state.failed ? <PageCrashed /> : this.props.children;
  }
}

function PageCrashed() {
  const { t } = useTranslation();
  return (
    <PageFrame layout={{}}>
      <div className={styles.root} role="alert">
        <p>{t('common.pageCrashed')}</p>
        <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
          {t('common.reload')}
        </Button>
      </div>
    </PageFrame>
  );
}
